import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import InsertSpanishPopup from 'containers/header/insert-spanish-popup';
import {
  changeWorksheet,
  updateWorksheetInput,
  updateModalVisibility
  // resetInputMap
} from 'state/actions/action-creators';
import { translate } from 'translations/translator';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { faEject } from '@fortawesome/free-solid-svg-icons';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { faSave } from '@fortawesome/free-solid-svg-icons';
const t = key => translate('worksheet', key);

const SheetFooter = ({ isLite, sectionId, handlePrint }) => {
  const classes = useStyles();
  const {
    container,
    popover,
    buttonCommon,
    insertSpanish,
    teachingTips,
    clearButton,
    openButton,
    printButton,
    saveButton,
    icon
  } = classes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editedWorksheet = useSelector((state: any) => state.worksheetMaker.editedWorksheet);
  const printSaveButtonActive = useSelector(
    (state: any) => state.worksheetMaker.printSaveButtonActive
  );
  const sectionsList = useSelector((state: any) => state.worksheetMaker.sections);
  const uploadRef: any = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClearClick = () => {
    const worksheetId = editedWorksheet.worksheetId;
    const valueMap = { ...editedWorksheet.worksheetMap };
    Object.keys(valueMap).forEach(key => (valueMap[key] = ''));
    dispatch(updateWorksheetInput(worksheetId, valueMap));
  };

  const handleSelectWorksheet = (sectionId, worksheetId) => {
    dispatch(changeWorksheet(worksheetId));
    const newPath = `/${sectionId}/${worksheetId}${location.search}`;

    navigate(newPath);
  };

  const handleOpenClick = event => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e: any) => {
      let contents = e.target.result;
      let jsonContent = JSON.parse(contents);

      handleSelectWorksheet(jsonContent.sectionId, jsonContent.worksheetId);
      setTimeout(
        () =>
          dispatch(
            updateWorksheetInput(jsonContent.worksheetId, jsonContent.currentWorksheetInput)
          ),
        0
      );
    };
    reader.readAsText(file);
  };

  const handlePrintClick = () => {
    if (printSaveButtonActive && handlePrint) {
      handlePrint();
    }
  };

  const download = (fileNameToSaveAs, textToWrite) => {
    /* Saves a text string as a blob file*/
    const ie = navigator.userAgent.match(/MSIE\s([\d.]+)/);
    const ie11 = navigator.userAgent.match(/Trident\/7.0/) && navigator.userAgent.match(/rv:11/);
    const ieEDGE = navigator.userAgent.match(/Edge/g);
    const ieVer = ie ? ie[1] : ie11 ? 11 : ieEDGE ? 12 : -1;

    const textFileAsBlob = new Blob([textToWrite], {
      type: 'text/plain'
    });

    if (+ieVer > -1) {
      if ((window.navigator as any).msSaveBlob) {
        (window.navigator as any).msSaveBlob(textFileAsBlob, fileNameToSaveAs);
      }
    } else {
      const downloadLink = document.createElement('a');

      downloadLink.download = fileNameToSaveAs;
      downloadLink.href = window.URL.createObjectURL(textFileAsBlob);
      downloadLink.onclick = (e: any) => {
        document.body.removeChild(e.target);
      };
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
    }
  };

  const handleSaveClick = () => {
    const currentSection = sectionsList.filter(item => item.id === parseInt(sectionId))[0];
    const currentWorksheet = currentSection.worksheetList.filter(
      item => item.id === editedWorksheet.worksheetId
    )[0];
    download(
      t(currentWorksheet.sheetName),
      JSON.stringify({
        sectionId,
        worksheetId: editedWorksheet.worksheetId,
        currentWorksheetInput: editedWorksheet.worksheetMap
      })
    );
  };

  return (
    <Box>
      <Popover
        slotProps={{
          paper: {
            className: popover
          }
        }}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <InsertSpanishPopup closePopup={() => setAnchorEl(null)} />
      </Popover>
      <Box className={container}>
        <Box px={1}>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            variant="contained"
            color="primary"
            className={`${insertSpanish} ${buttonCommon}`}
            onClick={(event: any) => setAnchorEl(event.currentTarget)}
          >
            <FontAwesomeIcon icon={faAngleUp} className={icon} />
            {t('insertSpanish')}
          </Button>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            color="primary"
            className={`${teachingTips} ${buttonCommon}`}
            onClick={() => dispatch(updateModalVisibility(true))}
          >
            <FontAwesomeIcon icon={faBuilding} className={icon} />
            {t('teachingTips')}
          </Button>
        </Box>
        <Box>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            color="secondary"
            className={`${clearButton} ${buttonCommon}`}
            onClick={handleClearClick}
          >
            <FontAwesomeIcon icon={faTrash} className={icon} />
            {t('clear')}
          </Button>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            color="primary"
            className={`${openButton} ${buttonCommon}`}
            onClick={() => {
              if (uploadRef?.current) {
                uploadRef?.current?.click?.();
              }
            }}
          >
            <input
              ref={uploadRef}
              id="file-upload"
              type="file"
              name="files[]"
              multiple={true}
              onChange={handleOpenClick}
              style={{ display: 'none' }}
            />
            <FontAwesomeIcon icon={faEject} className={icon} />
            {t('open')}
          </Button>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            color="primary"
            className={`${printButton} ${buttonCommon}`}
            disabled={!printSaveButtonActive}
            onClick={handlePrintClick}
          >
            <FontAwesomeIcon icon={faPrint} className={icon} />
            {t('print')}
          </Button>
          <Button
            disableFocusRipple
            disableRipple
            disableTouchRipple
            className={`${saveButton} ${buttonCommon}`}
            disabled={!printSaveButtonActive}
            onClick={handleSaveClick}
          >
            <FontAwesomeIcon icon={faSave} className={icon} />
            {t('download')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SheetFooter;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  popover: {
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    height: 'max-content',
    boxShadow: 'none',
    borderRadius: '10px',
    overflow: 'auto',
    border: '1px solid #000000'
  },
  buttonCommon: {
    marginRight: 8,
    marginLeft: 8,
    fontFamily: 'Futura-Semibold',
    textTransform: 'capitalize',
    '&:focus-visible': {
      borderRadius: '4px',
      outline: '2px solid #248AFF',
      outlineOffset: '4px'
    }
  },
  insertSpanish: {
    color: '#ffffff',
    backgroundColor: '#2e7bc2'
  },
  teachingTips: {
    color: '#2e7bc2'
  },
  clearButton: {
    color: '#d12348'
  },
  openButton: {
    color: '#2e7bc2'
  },
  printButton: {
    color: '#2e7bc2'
  },
  saveButton: {
    backgroundColor: '#D6D6D6',
    color: '#000000',
    '&:hover': {
      backgroundColor: '#000000',
      color: '#FFFFFF'
    }
  },
  icon: {
    marginRight: '5px'
  }
});
